import styled from '@emotion/styled';
import { Calendar, Truck, MapPin, CreditCard } from 'lucide-react';
import type { Order } from '@/domain/entities/order.entity';
import { formatOrderDate, formatFullAddress } from './utils';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  padding: ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const DetailRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  align-items: flex-start;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-top: 2px;
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

interface ShippingDetailsPanelProps {
  readonly order: Order;
}

export function ShippingDetailsPanel({ order }: Readonly<ShippingDetailsPanelProps>) {
  const { lines } = formatFullAddress(order.shipping_address);
  const paymentMethod = order.payments?.[0]?.gateway
    ? `${order.payments[0].gateway.charAt(0).toUpperCase()}${order.payments[0].gateway.slice(1)}`
    : 'N/A';

  return (
    <Panel>
      <DetailRow>
        <Calendar />
        <DetailContent>
          <DetailLabel>Order Placed</DetailLabel>
          <DetailValue>{formatOrderDate(order.purchase_date)}</DetailValue>
        </DetailContent>
      </DetailRow>

      <DetailRow>
        <Truck />
        <DetailContent>
          <DetailLabel>Shipping Method</DetailLabel>
          <DetailValue>{order.shipping_method?.title || 'Standard Shipping'}</DetailValue>
        </DetailContent>
      </DetailRow>

      <DetailRow>
        <MapPin />
        <DetailContent>
          <DetailLabel>Ship To</DetailLabel>
          <DetailValue>
            {order.shipping_address.first_name} {order.shipping_address.last_name}
          </DetailValue>
          {lines.map((line) => (
            <DetailValue key={line}>{line}</DetailValue>
          ))}
        </DetailContent>
      </DetailRow>

      <DetailRow>
        <CreditCard />
        <DetailContent>
          <DetailLabel>Payment Method</DetailLabel>
          <DetailValue>{paymentMethod}</DetailValue>
        </DetailContent>
      </DetailRow>
    </Panel>
  );
}
